import { InfiniteTable } from '@vrplatform/table';
import { LoadingIndicator } from '@vrplatform/components-legacy';
import { Center } from '@mantine/core';
import { type ComponentProps, useCallback, useMemo } from 'react';
import { type StoreApi, useStore } from 'zustand';
import {
  useTableStoreSelected,
  useTableStoreSetSelected,
} from '../../../../../stores';
import {
  ListingsTableFilter,
  useListingsTableFilter,
} from '../../../../listings/list/ledger/ListingsTableFilter';
import { useListingsTableColumns } from '../../../../listings/list/ledger/useListingsTableColumns';
import type { ListingRow } from '../../../../listings/list/ledger/useListingsTableQuery';
import { useListingsTableQuery } from '../../../../listings/list/ledger/useListingsTableQuery';
import type { StoreProps } from './_types';
import { formatListingToEntity } from './useExpenseListingReservationModalQuery';

type TableProps = ComponentProps<typeof InfiniteTable<ListingRow>>;

export const ListingTable = ({ store }: { store: StoreApi<StoreProps> }) => {
  const rowSelection = useTableStoreSelected();
  const setRowSelection = useTableStoreSetSelected();

  const setEntity = useStore(store, (st) => st.setEntity);
  const queryData = useListingsTableQuery();

  const { reset: resetFilter } = useListingsTableFilter();

  const columns = useListingsTableColumns();

  const handleSelection = useCallback(
    (selected: Record<string, boolean>) => {
      const oldSelection = Object.entries(rowSelection)
        .filter((x) => x[1])
        .map((x) => x[0]);
      const newSelection = Object.entries(selected)
        .filter((x) => x[1])
        .map((x) => x[0]);

      const newId = newSelection.find((x) => oldSelection.indexOf(x) === -1);

      if (!newSelection.length || !newId) {
        // boop
      } else {
        const allRows =
          queryData.data?.pages.flatMap((page) => page.list) || [];
        const selectedRow = allRows.find((row) => row.id === newId);

        if (selectedRow) {
          setRowSelection({ [newId]: true });
          setEntity({
            ...formatListingToEntity(selectedRow),
            index: allRows.indexOf(selectedRow),
          });
        }
      }
    },
    [queryData.data, rowSelection, setEntity, setRowSelection]
  );

  const table = useMemo<TableProps['table']>(() => {
    return {
      hideSettings: true,
      hidePagination: true,
      onRowClick: {
        handler: (row) => handleSelection({ [row.id]: true }),
      },
    };
  }, [handleSelection]);

  const selecting = useMemo<TableProps['selecting']>(() => {
    return {
      selectDisplayMode: 'radio',
      setAllPagesSelected: () => {},
      setRowSelection: (selected) => handleSelection(selected),
      rowSelection: { allPagesSelected: false, rows: rowSelection },
      disableSelectAll: true,
    };
  }, [handleSelection, rowSelection]);

  if (queryData.isInitialLoading || !queryData.data)
    return (
      <Center flex={1}>
        <LoadingIndicator size="sm" />
      </Center>
    );

  return (
    <InfiniteTable
      key="listing-table"
      queryData={queryData}
      columns={columns}
      resetFilter={resetFilter}
      table={table}
      selecting={selecting}
    >
      <ListingsTableFilter hide={['issue']} />
    </InfiniteTable>
  );
};
